<script setup>
import { useAuthStore } from '@/stores/authStore';
import { useProductStore } from '@/stores/productsStore';
import { useOrderStore } from '@/stores/OrderStore';
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue';
import { useToast } from 'vue-toastification';
import { useKeyboardStore } from '@/stores/keyboardStore';
import axios from 'axios';

const toast = useToast();
const authStore = useAuthStore();
const store = useProductStore();
const orderStore = useOrderStore();
const keyboardStore = useKeyboardStore();
const searchQuery = ref('');

// Function to get cached product from localStorage
const getCachedProduct = (barcode) => {
  const cachedProducts = JSON.parse(localStorage.getItem('cachedProducts') || '{}');
  return cachedProducts[barcode];
};

// Function to cache product in localStorage
const cacheProduct = (barcode, product) => {
  const cachedProducts = JSON.parse(localStorage.getItem('cachedProducts') || '{}');
  cachedProducts[barcode] = product;
  localStorage.setItem('cachedProducts', JSON.stringify(cachedProducts));
};

// Function to handle API request
const fetchProductByBarcode = async (query) => {
    if (!query || query.length < 3) return;

    const existingProduct = store.products.find(p => 
        p.Barcodes && p.Barcodes.includes(query)
    );

    if (existingProduct) {
        orderStore.addOrderItem(existingProduct);
        toast.success('Product found locally');
        searchQuery.value = '';
        return;
    }

    // Then check localStorage cache
    const cachedProduct = getCachedProduct(query);
    if (cachedProduct) {
        orderStore.addOrderItem(cachedProduct);
        toast.success('Product loaded from cache');
        searchQuery.value = '';
        return;
    }

    // Finally try API
    authStore.isBarcodeSearchFromBackend = true;
    try {
        const response = await axios.get(
            `/api/Order/GetProduct?barcode=${query}`,
            {
                headers: {
                    'store-id': authStore.branchId,
                    'Authorization': `Bearer ${authStore.managerToken ? authStore.managerToken : authStore.token}`
                }
            }
        );
        
        if (response.data.Status === 200 && response.data.Product) {
            // Cache the product for future use
            cacheProduct(query, response.data.Product);
            
            orderStore.addOrderItem(response.data.Product); 
            toast.success('Product loaded from server');
            searchQuery.value = '';
        } else {
            toast.error('Product Not Found!');
        }
    } catch (error) {
        console.error('API error:', error);
        toast.error('Failed to search for product');
    } finally {
        authStore.isBarcodeSearchFromBackend = false;
    }
};

// Watch for barcode search changes
watch(searchQuery, (newQuery) => {
    const query = newQuery.trim();
    if (!query) return;
});

// Computed property for filtered products (purely local)
const filteredProducts = computed(() => {
    if (!store.products || !searchQuery.value) {
        return []; 
    }

    const query = searchQuery.value.toLowerCase().trim();
    if (!query) return [];

    // Create a Map to store products by unique SKUs
    const productMap = new Map();

    // Filter products by Barcodes and add them to the Map (unique SKUs only)
    store.products.forEach(product => {
        if (product.Barcodes && product.Barcodes.some(barcode => barcode.toLowerCase().includes(query))) {
            productMap.set(product.Sku, product);
        }
    });

    // Filter products by RetailSku and add them to the Map (unique SKUs only)
    store.products.forEach(product => {
        if (product.RetailSku && product.RetailSku.toLowerCase().includes(query)) {
            if (!productMap.has(product.Sku)) {
                productMap.set(product.Sku, product);
            }
        }
    });

    // Filter products by Name and add them to the Map (ensuring unique SKUs)
    store.products.forEach(product => {
        if (product.Name && product.Name.toLowerCase().includes(query)) {
            if (!productMap.has(product.Sku)) {
                productMap.set(product.Sku, product);
            }
        }
    });

    return Array.from(productMap.values()).slice(0, 5);
});

function clearSearch() {
    searchQuery.value = '';
}

function handleClick(product) {
    orderStore.addOrderItem(product);
    toast.success('Item added to order');
    searchQuery.value = '';
}

// Keyboard shortcuts
const searchInput = ref(null);

// watch for loginns
watch(
  [() => authStore.isUserLoggedIn, () => authStore.isManagerLoggedIn],
  ([userLoggedIn, managerLoggedIn]) => {
    if (userLoggedIn) {
      nextTick(() => searchInput.value?.focus());
    }
    if (managerLoggedIn) {
      setAttribute();  
      nextTick(() => searchInput.value?.focus());
    }
    if(!managerLoggedIn) {
        removeAttribute();
    }
  }
);

// watch when order items are null
watch(
  () => orderStore.state.orderItems,
  (newItems) => {
    if (newItems.length === 0) {
      searchQuery.value = '';
      nextTick(() => searchInput.value?.focus());
    }
  },
  { deep: true }
);

onMounted(() => {
  if (authStore.isUserLoggedIn || authStore.isManagerLoggedIn) {
    nextTick(() => searchInput.value?.focus());
  }
  keyboardStore.register('Alt+B', () => searchInput.value?.focus());
});

onUnmounted(() => {
  keyboardStore.unregister('Alt+B');
});

function setAttribute () {
    searchInput.value.setAttribute("autocomplete", "off");
}

function removeAttribute () {
    searchInput.value.removeAttribute("autocomplete");
}


// Handle search item by skuu

const minCharacters = 5;

const responseData = ref([]);
const expectedProductCount = ref(0);
const isRequsetBeingMade = ref(false);

const fetchProductByRetailSku = async (query) => {
  if (!query) {
    toast.error('Please enter a valid SKU');
    return;
  }

  // Load from cached products if available
  const cachedProducts = JSON.parse(localStorage.getItem('cachedProducts') || '{}');
  const cachedProduct = Object.values(cachedProducts).find(
    product => product.RetailSku === query
  );

  if (cachedProduct) {
    orderStore.addOrderItem(cachedProduct);
    // toast.success('Product loaded from cache');
    searchQuery.value = '';
    return;
  }

  isRequsetBeingMade.value = true;
  responseData.value = [];
  expectedProductCount.value = 0;

  authStore.isRetailSkuSearchFromBackend = true;

  try {
    const response = await axios.get(
      `/api/Order/GetProductByRetailSkuSingle?retailSku=${query}`,
      {
        headers: {
          'store-id': authStore.branchId,
          'Authorization': `Bearer ${authStore.managerToken || authStore.token}`,
        }
      }
    );

    const product = response.data.Product || [];

    if (response.data.Status === 200) {
      expectedProductCount.value = product.length;
      responseData.value = product;
      toast.success('Product loaded from server');

      const rawProduct = product;
      const mappedProduct = {
        Barcodes: rawProduct.Barcodes,
        Sku: rawProduct.Sku,
        RetailSku: rawProduct.RetailSku,
        Name: rawProduct.ItemName,
        ImageUrl: rawProduct.ImageUrl,
        MaxQty: rawProduct.Quantity,
        Price: rawProduct.Price,
        // Discount: rawProduct.DiscountPrice,
        isNonStocking: rawProduct.NonStocking,
        Comment: rawProduct.Comment,
      };

      orderStore.addOrderItem(mappedProduct);
      cacheProduct(query, mappedProduct);
      searchQuery.value = '';
    } else {
      toast.error('Product Not Found!');
    }

  } catch (error) {
    console.error('Error fetching product:', error);
    toast.error('Error fetching product');
  } finally {
    isRequsetBeingMade.value = false;
    authStore.isRetailSkuSearchFromBackend = false;
  }
};


function handleEnterKey() {
    const query = searchQuery.value.trim();
    
    const isSearchQueryOnlyNumbers = /^\d+$/.test(query);
    
    if(isSearchQueryOnlyNumbers) {
        // First try to find locally
        if (filteredProducts.value.length > 0) {
            const firstProduct = filteredProducts.value[0];
            handleClick(firstProduct);
        } 
        // If not found locally, try cache or API
        else if (query.length >= 3) {
            fetchProductByBarcode(query);
        }
    } else {
        if (query.length < minCharacters) {
          toast.error(`Please enter at least ${minCharacters} characters to search the full catalog.`);
          return;
        }

        if (query) {
          fetchProductByRetailSku(searchQuery.value);
        }
    }
}

function handleArrowKeys(e) {
  // Right arrow Add MISC-2
  if (e.key === 'ArrowRight' && searchQuery.value === '') {
    const misc = store.products.find(p => p.Sku === '2');
    if (misc) {
      orderStore.addOrderItem(misc);
      toast.success('MISC item added');
    }
    e.preventDefault();
    return;
  }

  // Left arrow Add TIP-99
  if (e.key === 'ArrowLeft' && searchQuery.value === '') {
    const tip = store.products.find(p => p.Sku === 'TIP-99');
    if (tip) {
      orderStore.addOrderItem(tip);
      toast.success('Tip item added');
    }
    e.preventDefault();
    return;
  }
}


</script>

<template>
    <div class="relative w-full">
        <input 
            ref="searchInput"
            autocomplete="off"
            v-model="searchQuery" 
            :disabled="!(authStore.isUserLoggedIn || authStore.isManagerLoggedIn)" 
            type="text"
            name="barcode-search" 
            id="barcode-search"
            class="bg-gray-100 border-0 outline-none p-2 rounded-lg w-full pr-10"
            :class="{ 'opacity-50 cursor-not-allowed': !(authStore.isUserLoggedIn || authStore.isManagerLoggedIn) }"
            placeholder="Scan or Enter Barcode, SKU, or Item Name"
            @keydown.enter="handleEnterKey"
            @keydown="handleArrowKeys"
        >

        <!-- Clear Button (X) -->
        <button v-if="searchQuery" @click="clearSearch"
            class="absolute right-2 top-2 text-gray-500 hover:text-gray-700">
            <i class="pi pi-times"></i>
        </button>
    </div>

    <div v-if="searchQuery.value && filteredProducts.length === 0">
        <p>No products found.</p>
    </div>

    <div v-else-if="filteredProducts.length > 0"
        class="absolute top-full left-0 overflow-y-auto w-full bg-white shadow-xl rounded-2xl z-50"
        style="max-height: 620px;">
        <table class="min-w-full bg-white border border-gray-200">
            <thead>
                <tr class="border-b bg-gray-100">
                    <th class="py-2 px-4 text-left text-gray-600 font-medium">Image</th>
                    <th class="py-2 px-4 text-left text-gray-600 font-medium">Item Name</th>
                    <th class="py-2 px-4 text-left text-gray-600 font-medium">Retail Sku</th>
                    <th class="py-2 px-4 text-left text-gray-600 font-medium">QOH Qty</th>
                    <th class="py-2 px-4 text-left text-gray-600 font-medium">Price</th>
                </tr>
            </thead>
            <tbody>
                <tr @click="handleClick(product)" v-for="product in filteredProducts" :key="product.Sku"
                    class="border-t cursor-pointer hover:bg-gray-50">
                    <td class="py-1 px-4">
                        <img :src="`${product.ImageUrl}`"
                            alt="Product Image" class="w-16 h-16 object-cover">
                    </td>
                    <td class="py-1 px-4">{{ product.Name }}</td>
                    <td class="py-1 px-4">{{ product.RetailSku }}</td>
                    <td class="py-1 px-4">{{ product.MaxQty }}</td>
                    <td class="py-1 px-4">{{ product.Price }}</td>
                </tr>
            </tbody>
        </table>
    </div>
</template>


<style scoped>

</style>